import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { getCookie } from '@/utils/helperFunctions';
import { CacheKeys } from '@/utils/cacheKeys';

interface AuthContextType {
  isAuthenticated: boolean;
  ssoSupportToken: string | null;
  setAuthState: (authenticated: boolean, token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [ssoSupportToken, setSsoToken] = useState<string | null>(null);

  // Initialize auth state from cookies
  useEffect(() => {
    const checkAuthState = () => {
      const authenticated = getCookie(CacheKeys.isAuthenticated) === 'true';
      const token = getCookie(CacheKeys.ssoSupportToken) || null;

      setIsAuthenticated(authenticated);
      setSsoToken(token);
    };

    checkAuthState();

    // Poll for auth changes (for logout detection)
    const interval = setInterval(checkAuthState, 1000);
    return () => clearInterval(interval);
  }, []);

  const setAuthState = (authenticated: boolean, token: string | null) => {
    setIsAuthenticated(authenticated);
    setSsoToken(token);
  };

  const value: AuthContextType = useMemo(
    () => ({
      isAuthenticated,
      ssoSupportToken,
      setAuthState
    }),
    [isAuthenticated, ssoSupportToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
